import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Sparkles, Star } from "lucide-react";
import Layout from "../../components/Layout/Layout";
import { sendAuraSurvey } from "../../api/aiagent/auraApi";

const RadioGroup = ({ options, value, onChange }) => (
    <div className="mt-3 flex flex-wrap gap-2">
        {options.map((option) => (
            <button
                key={option}
                type="button"
                onClick={() => onChange(option)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                    value === option
                        ? "border-violet-300 bg-violet-600 text-white shadow-[0_8px_20px_rgba(124,58,237,0.25)]"
                        : "border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:bg-violet-50"
                }`}
            >
                {option}
            </button>
        ))}
    </div>
);

const ScaleRow = ({ value, onChange, minLabel, maxLabel }) => (
    <div className="mt-3 flex items-center gap-3">
        <span className="w-24 shrink-0 text-right text-xs text-slate-500">{minLabel}</span>
        <div className="flex flex-1 justify-center gap-3 sm:gap-5">
            {[1, 2, 3, 4, 5].map((score) => (
                <button
                    key={score}
                    type="button"
                    onClick={() => onChange(score)}
                    aria-label={`${score}`}
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition ${
                        value === score
                            ? "border-violet-300 bg-violet-600 text-white shadow-[0_8px_20px_rgba(124,58,237,0.25)]"
                            : "border-slate-200 bg-white text-slate-500 hover:border-violet-300 hover:bg-violet-50"
                    }`}
                >
                    {score}
                </button>
            ))}
        </div>
        <span className="w-24 shrink-0 text-xs text-slate-500">{maxLabel}</span>
    </div>
);

const StarRating = ({ value, onChange }) => (
    <div className="mt-3 flex gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
            <button key={star} type="button" onClick={() => onChange(star)} aria-label={`${star} star${star === 1 ? "" : "s"}`}>
                <Star className={`h-7 w-7 transition ${star <= value ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-300"}`} />
            </button>
        ))}
    </div>
);

const QuestionCard = ({ label, required, error, children }) => (
    <div className={`rounded-[22px] border bg-white/70 p-5 shadow-sm backdrop-blur-xl transition ${error ? "border-rose-300" : "border-white/90"}`}>
        <p className="text-sm font-semibold text-slate-800">
            {label}
            {required ? <span className="ml-1 text-rose-500">*</span> : null}
        </p>
        {children}
        {error ? <p className="mt-2 text-xs text-rose-500">This field is required.</p> : null}
    </div>
);

const defaultForm = {
    answered_question: "",
    satisfactory_first_try: "",
    good_length_and_structure: "",
    relevance_score: 0,
    helpfulness_score: 0,
    clarity_score: 0,
    language_score: 0,
    overall_rating: 0,
    comments: "",
};

const FeedbackSurvey = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = searchParams.get("session_id") || "";
    const chatId = searchParams.get("chat_id") || "";

    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const set = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
        setErrors((current) => ({ ...current, [field]: false }));
    };

    const validate = () => {
        const nextErrors = {};
        if (!form.answered_question) nextErrors.answered_question = true;
        if (!form.satisfactory_first_try) nextErrors.satisfactory_first_try = true;
        if (!form.good_length_and_structure) nextErrors.good_length_and_structure = true;
        if (!form.relevance_score) nextErrors.relevance_score = true;
        if (!form.helpfulness_score) nextErrors.helpfulness_score = true;
        if (!form.clarity_score) nextErrors.clarity_score = true;
        if (!form.overall_rating) nextErrors.overall_rating = true;
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true);
        try {
            await sendAuraSurvey({ sessionId, chatId, ...form });
        } catch {
            // proceed to success regardless — the survey isn't critical-path
        } finally {
            setIsSubmitting(false);
            setIsSubmitted(true);
        }
    };

    if (isSubmitted) {
        return (
            <Layout activeLabel="Aura">
                <div className="flex min-h-[calc(100vh-73px)] items-center justify-center bg-gradient-to-br from-[#f3f6ff] via-[#f0edff] to-[#fff3fa] px-4">
                    <div className="max-w-sm rounded-[28px] border border-white/90 bg-white/80 p-10 text-center shadow-[0_26px_90px_rgba(79,70,229,0.2)] backdrop-blur-xl">
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-violet-100 text-violet-600">
                            <CheckCircle2 className="h-7 w-7" />
                        </div>
                        <h2 className="mt-4 text-lg font-semibold text-slate-900">Thank you!</h2>
                        <p className="mt-2 text-sm text-slate-500">Your feedback helps us make Aura better for every creator.</p>
                        <button
                            type="button"
                            onClick={() => navigate("/aura-ai-assistant")}
                            className="mt-6 rounded-full bg-violet-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-violet-700"
                        >
                            Back to Aura
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout activeLabel="Aura">
            <div className="min-h-[calc(100vh-73px)] bg-gradient-to-br from-[#f3f6ff] via-[#f0edff] to-[#fff3fa] px-4 py-8">
                <div className="mx-auto max-w-2xl space-y-4">
                    <div className="overflow-hidden rounded-[22px] border border-white/90 bg-white/80 shadow-sm backdrop-blur-xl">
                        <div className="h-1.5 w-full bg-gradient-to-r from-violet-600 to-cyan-500" />
                        <div className="px-6 py-6">
                            <div className="flex items-center gap-2 text-violet-600">
                                <Sparkles className="h-5 w-5" />
                                <p className="text-xs font-semibold uppercase tracking-[0.14em]">Aura feedback</p>
                            </div>
                            <h1 className="mt-2 text-2xl font-semibold text-slate-900">Tell us more about this response</h1>
                            <p className="mt-2 text-sm leading-relaxed text-slate-500">A few extra questions to help us understand how Aura is doing. It only takes a minute.</p>
                            <p className="mt-4 text-xs text-rose-500">* Required</p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <QuestionCard label="Did Aura answer your question?" required error={errors.answered_question}>
                            <RadioGroup options={["Yes", "No", "Partial"]} value={form.answered_question} onChange={(value) => set("answered_question", value)} />
                        </QuestionCard>

                        <QuestionCard label="Was the first answer satisfactory?" required error={errors.satisfactory_first_try}>
                            <RadioGroup options={["Yes", "No"]} value={form.satisfactory_first_try} onChange={(value) => set("satisfactory_first_try", value)} />
                        </QuestionCard>

                        <QuestionCard label="Was it a good length and well-structured?" required error={errors.good_length_and_structure}>
                            <RadioGroup options={["Yes", "No"]} value={form.good_length_and_structure} onChange={(value) => set("good_length_and_structure", value)} />
                        </QuestionCard>

                        <QuestionCard label="How relevant was the response to your question?" required error={errors.relevance_score}>
                            <ScaleRow value={form.relevance_score} onChange={(value) => set("relevance_score", value)} minLabel="Not relevant" maxLabel="Very relevant" />
                        </QuestionCard>

                        <QuestionCard label="How helpful was the response?" required error={errors.helpfulness_score}>
                            <ScaleRow value={form.helpfulness_score} onChange={(value) => set("helpfulness_score", value)} minLabel="Not helpful" maxLabel="Very helpful" />
                        </QuestionCard>

                        <QuestionCard label="How clear was the response?" required error={errors.clarity_score}>
                            <ScaleRow value={form.clarity_score} onChange={(value) => set("clarity_score", value)} minLabel="Confusing" maxLabel="Very clear" />
                        </QuestionCard>

                        <QuestionCard label="How was the language and tone? (optional)">
                            <ScaleRow value={form.language_score} onChange={(value) => set("language_score", value)} minLabel="Needs work" maxLabel="Excellent" />
                        </QuestionCard>

                        <QuestionCard label="Overall, how would you rate this response?" required error={errors.overall_rating}>
                            <StarRating value={form.overall_rating} onChange={(value) => set("overall_rating", value)} />
                        </QuestionCard>

                        <QuestionCard label="Anything else you'd like to add? (optional)">
                            <textarea
                                value={form.comments}
                                onChange={(event) => set("comments", event.target.value)}
                                rows={3}
                                placeholder="Share any other thoughts..."
                                className="mt-3 w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none placeholder:text-slate-400 focus:border-violet-300 focus:ring-2 focus:ring-violet-100"
                            />
                        </QuestionCard>

                        <div className="flex items-center justify-between pb-6 pt-2">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="rounded-full bg-violet-600 px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 disabled:opacity-50"
                            >
                                {isSubmitting ? "Submitting…" : "Submit survey"}
                            </button>
                            <button type="button" onClick={() => navigate("/aura-ai-assistant")} className="text-sm font-medium text-slate-500 hover:text-violet-600">
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default FeedbackSurvey;
